namespace Quote {

    type QuoteProductType = Quote.QuoteProductType;
    type IQuoteProductHotel = Quote.IQuoteProductHotel;
    type IQuoteProductFlight = Quote.IQuoteProductFlight;
    type IQuoteProductTransfer = Quote.IQuoteProductTransfer;
    type IQuoteProductCarHire = Quote.IQuoteProductCarHire;
    type IQuoteSearch = Quote.IQuoteSearch;
    type IQuotePriceDifference = Quote.IQuotePriceDifference;

    export class QuoteService {
        private domain: any;
        private url: string = '/api2/quotes/';

        private basketQuote: any = {};
        private cachedQuoteSearch: IQuoteSearch | null = null;

        constructor(
            private $http: ng.IHttpService,
            private $window: ng.IWindowService,
            private broker: Core.Broker,
            private $q: ng.IQService,
            private dateUtil: any,
            private basketStore,
            private localStorageProvider: any
        ) {
            this.domain = $window.TRPCONFIG.Domain;
        }

        setTrackingTag(tag: string) : ng.IPromise<any> {
            return this.localStorageProvider.setItem('trackingTag', tag);
        }

        getTrackingTag(cb) : () => string | null | Error {
            return this.localStorageProvider.getItem('trackingTag', (error, value) => cb(value));
        }

        removeTrackingTag() : ng.IPromise<any> {
            return this.localStorageProvider.removeItem('trackingTag');
        }

        getQuoteByReference(quoteRef: string): ng.IPromise<IQuote> {
            return this.$http.get(this.url + 'getbyreference'+'?reference=' + quoteRef + '&domainId=' +
                this.domain.DomainId, { ignoreErrors: true }).then((res) => {
                    return res.data;
                });
        }

        refreshQuote(quoteId: string , productIds: string[] ): ng.IPromise<IQuote> {
            return this.$http.post<IQuote>(this.url + 'refreshproducts',
                    { QuoteId: quoteId, ProductIds: productIds },
                    { ignoreErrors: true }
                ).then((res) => {
                    this.basketStore.destroyBasket();
                    TRPCONFIG.BasketId = res.data.BasketId;
                    this.broker.publish('quoteLoadedToBasket');
                    return res.data;
                }, (res) => {
                    return {Error: res.data};
                });
        }

        createQuote(basketId:string, sharedUseFlag, cache: boolean = true): ng.IPromise<IQuote> {
            if(this.basketQuote[basketId]) {
                return this.basketQuote[basketId];
            }
            return this.$q((res, rej) => {
                this.getTrackingTag(value => {
                    let params: IQuoteCreateRequest = {
                        BasketId: basketId,
                        SharedUse: sharedUseFlag,
                        QuoteId: '',
                        TrackingTag: value
                    };
                    res(this.create(params, cache));
                });
            });
        }

        create(params: IQuoteCreateRequest, cache: boolean = true): ng.IPromise<IQuote>{
            params.DomainId = params.DomainId || this.domain.DomainId;
            params.CultureCode = params.CultureCode || this.domain.CultureCode;

            this.basketQuote[params.BasketId || ''] = this.$http.post(this.url + 'create', params, {
                cache,
                ignoreErrors: true
            }).then((res) => {
                this.removeTrackingTag();
                return res.data;
            }, (res) => {
                return {Error: res.data};
            });

            return this.basketQuote[params.BasketId || ''];
        }

        get(quoteId: string): ng.IPromise<IQuote> {
            return this.$http.get(this.url + 'get' + '?quoteId=' + quoteId,
                {cache: true, ignoreErrors : true}).then((res) => {
                return res.data;
            });
        }

        replaceProduct(quoteId: string, productId: string, replacementId): ng.IPromise<IQuote> {
            return this.$http.post(this.url+'replaceproduct', {
                QuoteId: quoteId,
                ProductId: productId,
                ReplacementId: replacementId
            }, { ignoreErrors: true }).then((res) => {
                return res.data;
            });
        }

        addBookings(quoteId: string, bookingIds: number[]): ng.IPromise<IQuote>{
            return this.$http.post(this.url+'addbookings', {
                QuoteId: quoteId,
                BookingIds: bookingIds
            }, { ignoreErrors: true }).then((res) => {
                return res.data;
            }, () => {
                return true;
            });
        }

        linkQuote(bookingReferences: string[], quoteId: string): ng.IPromise<IQuote | {}> {
            if(quoteId){
                let bookingIds: number[] = bookingReferences.map(function(ref: string){
                    return parseInt(ref.replace(/[^0-9]/gi, ''), 10);
                });
                return this.addBookings(quoteId, bookingIds);
            } else {
                return this.$q.when({});
            }
        }

        addNote(quoteId: string, noteText: string): ng.IPromise<IQuote> {
            return this.$http.post(this.url + 'addnote',
                    { QuoteId: quoteId, NoteText: noteText },
                    { ignoreErrors: true })
                .then((res) => {
                    return res.data;
                }, () => {
                    return this.$q.when({});
                });
        }

        private validateSearchModel(model: IQuoteSearchModel): boolean {
            const empty = v => !_.toString(v);

            const today = this.dateUtil.todayPlus(0, 0, true);

            const isDatesAreInRange = (start, end) =>
                !empty(start) && !empty(end) && this.dateUtil.dayDiff(start, end) <= 30;

            const isQuotedByDatesAreInRange = (start, end) =>
                (!empty(start) && this.dateUtil.dayDiff(start, today) <= 30)
                || isDatesAreInRange(start, end);

            return empty(model.quoteRef)
                && !isQuotedByDatesAreInRange(model.from,model.to)
                && empty(model.quotedById)
                && empty(model.userId)
                && empty(model.destination)
                && empty(model.externalAgentId)
                && empty(model.hotelName);
        }

        search(request: IQuoteSearchModel): ng.IPromise<IQuoteSearchResponse> {
            this.cachedQuoteSearch = null;
            return this.validateSearchModel(request)
                ? this.$q.reject({ status: 400 })
                : this.$http.get(this.url + 'search?' + $.param(request),
                    { ignoreErrors: true }).then((res: any) => {
                        this.cachedQuoteSearch = {
                            searchModel: request,
                            searchResponse: res.data
                        };
                        return res.data;
                    });
        }

        getCachedSearch():IQuoteSearch | null {
            return this.cachedQuoteSearch;
        }

        getTravelAgentNameForQuote(quote: Quote.IQuoteSearchResult): string | null {
            if (quote && (quote.TravelAgentFirstName || quote.TravelAgentLastName)) {
                return [quote.TravelAgentTitle, quote.TravelAgentFirstName, quote.TravelAgentLastName]
                    .filter(i => null != i).join(' ');
            }
            return null;
        }

        getCustomerNameForQuote(quote: Quote.IQuoteSearchResult, contact:any): string | null {
            if (contact) {
                return `${contact.Title} ${contact.FirstName} ${contact.LastName}`;
            } else if (quote && quote.CustomerFirstName && quote.CustomerLastName) {
                return `${quote.CustomerFirstName} ${quote.CustomerLastName}`;
            }
            return null;
        }

        getCustomerEmailForQuote(quote: Quote.IQuoteSearchResult, contact: any): string | null {
            if (contact && contact.Email) {
                return contact.Email;
            } else if (quote && quote.CustomerEmailAddress) {
                return quote.CustomerEmailAddress;
            }
            return null;
        }

        getCustomerPhoneNumberForQuote(contact: any): string | null {
            return contact && (contact.MobileTel || contact.HomeTel || contact.Phone || contact.CustomerPhoneNumber);
        }

        getCustomerSkywardNumberForQuote(data: any): string | null {
            if (data && (data.CustomerLoyaltyNumber || data.LoyaltyId)) {
                return data.CustomerLoyaltyNumber || data.LoyaltyId;
            }
            return null;
        }

        public getPriceDifference(products): IQuotePriceDifference {
            let totalBaseCost = 0;
            let priceDifferenceAbs = 0;
            products.forEach((product) => {
                totalBaseCost += product.QuotedCost;
                priceDifferenceAbs += (product.CurrentCost - product.QuotedCost);
            });
            const priceDifferencePercentage = (priceDifferenceAbs / totalBaseCost) * 100;
            return {
                priceDifferenceAbs,
                priceDifferencePercentage
            };
        }

        private getEarliestDate(earliestDate: string, date: string): string {
            const emptyValue: string = '';
            const compareDatesResult: boolean = this.dateUtil.compareDates(date, earliestDate) === -1;
            return (earliestDate === emptyValue) || compareDatesResult ? date : earliestDate;
        }

        private extractEarliestDate(products: any): string {
            const emptyValue: string = '';

            return _.reduce(products, (acc: string, p: any) => {
                let startDate: string;
                switch (p.TypeId) {
                    case QuoteProductType.Accommodation:
                        startDate = (<IQuoteProductHotel>p).CheckInDate;
                        break;
                    case QuoteProductType.Flight:
                        startDate = (<IQuoteProductFlight>p).OutboundLeg.Segments[0].DepartureDate;
                        break;
                    case QuoteProductType.Transfer:
                        startDate = (<IQuoteProductTransfer>p).OutboundDate || emptyValue;
                        break;
                    case QuoteProductType.CarHire:
                        startDate = (<IQuoteProductCarHire>p).PickUpDate;
                        break;
                    default:
                        startDate = emptyValue;
                        break;
                }
                return this.getEarliestDate(acc, startDate);
            }, emptyValue);
        }

        public validForDays(refreshedQuoteProducts) {
            const validForDaysLength = 30;
            const earliestDate = this.extractEarliestDate(refreshedQuoteProducts);

            if (earliestDate.length > 0) {
                const days = this.dateUtil.getDifferenceInDays(new Date(), this.dateUtil.parseDate(earliestDate));
                if (days < validForDaysLength) {
                    return days;
                }
            }

            return validForDaysLength;
        }

    }

    angular
        .module('TR')
        .service('quoteService', QuoteService);
}
